import { Auth } from "@aws-amplify/auth";

import { IsProd } from "@/constants/config";
import { delay } from "@/utils";
import request from "@/utils/request";

const PATH_PREFIX = IsProd ? "/v1/" : "/vb/";

function getCookie(key: string) {
  const b = document.cookie.match("(^|;)\\s*" + key + "\\s*=\\s*([^;]+)");
  return b ? b.pop() : "";
}

export interface CreateAlertActionParams {
  filter_criteria: {
    cabins: string[];
    banks: string[];
    airline_programs: string[];
    max_points: number;
    mixed_cabin: number;
    flight_number: string;
    tax: string;
    departure_time: number[];
  };
  info: {
    passengers: number;
    children: number;
    departure: string[];
    arrival: string[];
    departureDate: string;
    departureDateSec: string;
    hotelName: string[];
  };
}
export const createAlertAction = (data: CreateAlertActionParams) => {
  return request.post<CreateAlertActionParams, { alert_set: number; max_alert: number }>(
    PATH_PREFIX + "alert_add",
    {
      ...data,
    }
  );
};

export interface ApiAlertItem {
  alert_id: string;
  alert_type?: string;
  deleted: boolean;
  email: string;
  expired: boolean;
  entries: string[];
  infinite_mode: boolean;
  data: {
    filter_criteria: {
      cabins: string[];
      max_points: string;
      passengers: string;
      children: number;
      stops: string;
      banks: string[];
      airlines: string[];
    };
  };
  filters: any;
  info: {
    arrival: string[];
    departure: string[];
    departureDate: string;
    departureDateSec: string;
    passengers: number;
    children: number;
    cabins: string;
    hotelName: string[];
  };
  flight_pts: string;
  generate_time: string;
  has_update: boolean;
  last_alert_time: string;
  latest_entry_date: string;
  user_id: string;
  valid: boolean;
  viewed: boolean;
  summary: {
    total_flights: number;
    total_matching_flights: number;
    least_miles: number;
    update_time: string;
    least_tax: number;
    program: string;
    card: string;
  };
  phone_number: {
    area: string;
    number: string;
    agreeReceiveSMS: boolean;
  };
}

export const getAlertAction = () => {
  return request.post<
    void,
    {
      max_alert: number;
      alert_set: number;
      items: ApiAlertItem[];
      success: boolean;
    }
  >(PATH_PREFIX + "alert_all", {});
};

export const deleteAlertAction = (data: { alert_id: string }) => {
  return request.post(PATH_PREFIX + "alert_delete", {
    ...data,
  });
};

export const modifyAlertAction = (data: { alert_id: string } & CreateAlertActionParams) => {
  return request.post<CreateAlertActionParams, { alert_set: number; max_alert: number }>(
    PATH_PREFIX + "alert_modify",
    {
      ...data,
    }
  );
};

export const toggleAlertStatusAction = (data: { alert_id: string; status: boolean }) => {
  return request.post(PATH_PREFIX + "alert_status", {
    ...data,
  });
};

export const toggleAlertViewAction = (data: { alert_id: number }) => {
  return request.post(PATH_PREFIX + "alert_viewed", {
    ...data,
  });
};

export const toggleAlertModeAction = (data: { alert_id: number; status: boolean }) => {
  return request.post(PATH_PREFIX + "alert_mode", {
    ...data,
  });
};

export const getUsageAction = () => {
  return request.post<unknown, { max_alert: number; alert_set: number }>(
    PATH_PREFIX + "alert_usage",
    {}
  );
};

export const getUserInfoAction = async () => {
  // Check if the user has returned from stripe page
  const info: any = {};
  // Check if there is any referral code stored in the local storage
  const referralStr = localStorage.getItem("referralCode");
  if (referralStr) {
    const referralInfo = JSON.parse(referralStr);

    if (referralInfo && referralInfo["expire_time"] >= new Date().getTime()) {
      info["referral_code"] = referralInfo["referralCode"];
    }
  }

  const update_info_field = localStorage.getItem("update_info");
  if (update_info_field) {
    const info_json = JSON.parse(update_info_field);
    if (info_json["expire_time"] > new Date().getTime()) {
      info["update_info"] = true;
    } else {
      localStorage.removeItem("update_info");
    }
  }

  // Rate limiter for user_info for 0.5s seconds to prevent duplicate calls
  const last_call_time = localStorage.getItem("last_call");
  if (last_call_time && new Date().getTime() - parseInt(last_call_time) < 100 * 5) {
    // Sleep for 0.5s
    await delay(500);
  } else {
    localStorage.setItem("last_call", new Date().getTime().toString());
  }

  const td_cookie = getCookie("trakdesk_cid");
  if (td_cookie) {
    info["cid"] = JSON.parse(td_cookie)["cid"];

    const trackdesk_sid = localStorage.getItem("trackdesk_source_id");
    info["trackdesk_sid"] = trackdesk_sid;
  } else {
    info["cid"] = "";
  }
  try {
    const session = await Auth.currentSession();
    const jwtToken = session.getIdToken().getJwtToken();
    await localStorage.setItem("jwtToken", jwtToken);
  } catch {
    // console.log("Flag 1 - User is not logged in");
    await localStorage.setItem("jwtToken", "");
  }

  const response_res = await request.post<
    unknown,
    {
      user_type: number;
      closest_airport: string;
      email: string;
      expire_date: string;
      referral_code: string;
      referral: object;
      influencer: boolean | undefined;
      user_preference: {
        cards: string[];
        programs: string[];
        cabins: string;
      };
      cid: string;
      promotion: {
        code: string;
        title: string;
        text: string;
        expire: number;
      };
      multicity_codes: Record<string, { description: string; airports: string[] }>;
    }
  >(PATH_PREFIX + "user_info", info);

  return response_res;
};

export interface AlertSearchHistoryItem {
  alert_id: string;
  matching_routes_count: string;
  new_routes_count: string;
  record_type: string;
  timestamp: string;
  ttl_timestamp: string;
}
export const getAlertSearchHistory = (data: { alert_id: string }) => {
  return request.post<unknown, { data: AlertSearchHistoryItem[] }>(
    PATH_PREFIX + "alert_search_history",
    data
  );
};
